import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export default function Hero() {
  const [scrollY, setScrollY] = useState(0);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-slate-50 pt-20 md:pt-0">
      {/* Animated Background Elements with Parallax */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-20 left-10 w-48 h-48 md:w-72 md:h-72 bg-blue-200/20 rounded-full blur-[100px] animate-pulse"
          style={{ transform: `translateY(${scrollY * 0.3}px)` }}
        ></div>
        <div
          className="absolute bottom-20 right-10 w-64 h-64 md:w-96 md:h-96 bg-emerald-200/20 rounded-full blur-[100px] animate-pulse"
          style={{ animationDelay: '1s', transform: `translateY(${scrollY * -0.2}px)` }}
        ></div>
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] md:w-[600px] md:h-[600px] bg-blue-100/30 rounded-full blur-3xl"
          style={{ transform: `translate(-50%, -50%) scale(${1 + scrollY * 0.0005})` }}
        ></div>
      </div>

      <div className="relative max-w-[1320px] mx-auto px-4 sm:px-6 py-16 md:py-32 lg:py-40">
        <div className="grid lg:grid-cols-2 gap-10 md:gap-14 lg:gap-16 xl:gap-24 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left space-y-4 sm:space-y-6 md:space-y-8 animate-fade-in relative z-10">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-white/50 backdrop-blur-sm rounded-full border border-blue-200 shadow-sm">
              <div className="w-2 h-2 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] rounded-full animate-pulse shadow-[0_0_8px_rgba(14,165,233,0.5)]"></div>
              <span className="text-xs sm:text-sm font-medium text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A] tracking-wider uppercase" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                {t('hero.badge')}
              </span>
            </div>

            <h1 className="text-[2rem] min-[380px]:text-[2.5rem] leading-[1.1] sm:text-5xl md:text-5xl lg:text-[2.6rem] xl:text-5xl 2xl:text-6xl font-bold text-[#0F172A] px-2 lg:px-0" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
              <span className="whitespace-nowrap">{t('hero.title')}</span> <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A] animate-pulse whitespace-nowrap">
                {t('hero.titleHighlight')}
              </span>
            </h1>

            {/* Subline */}
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-slate-600 leading-relaxed max-w-xl mx-auto lg:mx-0 px-2 lg:px-0" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              {t('hero.subtitle')}
            </p>



            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center lg:justify-start px-2 lg:px-0">
              <a
                href="/#preise"
                className="group px-5 py-2.5 sm:px-6 sm:py-3 md:px-8 md:py-4 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] text-white rounded-lg hover:bg-blue-900 transition-all duration-300 hover:scale-105 whitespace-nowrap font-bold text-sm sm:text-base md:text-lg flex items-center justify-center gap-2 shadow-md hover:shadow-lg inline-block"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                {t('hero.ctaPrimary')}
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </a>
              <Link
                to="/demo"
                className="group px-5 py-2.5 sm:px-6 sm:py-3 md:px-8 md:py-4 border border-slate-200 bg-white text-slate-700 rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-all duration-300 whitespace-nowrap font-semibold text-sm sm:text-base md:text-lg flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                <i className="ri-play-circle-line text-base sm:text-lg md:text-xl group-hover:scale-110 transition-transform text-white"></i>
                {t('hero.ctaSecondary')}
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 md:gap-6 justify-center lg:justify-start pt-2 sm:pt-4 px-2 lg:px-0">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2">
                  <div className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center text-[10px] text-slate-600 font-medium">CN</div>
                  <div className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center text-[10px] text-slate-600 font-medium">AB</div>
                  <div className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center text-[10px] text-slate-600 font-medium">XY</div>
                </div>
                <span className="text-xs sm:text-sm text-slate-500" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                  <strong className="text-[#0F172A]">500+</strong> {t('hero.trustCustomers')}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <i className="ri-star-fill text-[#FABB05] text-xs sm:text-sm md:text-base"></i>
                <i className="ri-star-fill text-[#FABB05] text-xs sm:text-sm md:text-base"></i>
                <i className="ri-star-fill text-[#FABB05] text-xs sm:text-sm md:text-base"></i>
                <i className="ri-star-fill text-[#FABB05] text-xs sm:text-sm md:text-base"></i>
                <i className="ri-star-fill text-[#FABB05] text-xs sm:text-sm md:text-base"></i>
                <span className="text-xs sm:text-sm text-slate-500 ml-1 sm:ml-2" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                  4.9/5 {t('hero.trustRating')}
                </span>
              </div>
            </div>
          </div>

          {/* Right Content - Dashboard Preview */}
          <div className="relative animate-fade-in-up mt-8 lg:mt-0 px-2 sm:px-4 lg:px-0 z-10 w-full max-w-xl mx-auto lg:max-w-none lg:ml-auto">

            {/* Dashboard Card */}
            <div className="relative bg-white/90 backdrop-blur-xl rounded-xl sm:rounded-2xl md:rounded-3xl shadow-2xl p-4 sm:p-5 md:p-8 border border-slate-200 hover:border-blue-300 transition-colors duration-500 group">
              {/* Header */}
              <div className="flex items-center justify-between mb-3 sm:mb-4 md:mb-6">
                <div>
                  <h3 className="text-base sm:text-lg md:text-xl font-bold text-[#0F172A] tracking-wide" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                    {t('hero.dashboardTitle')}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-500" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                    {t('hero.dashboardPeriod')}
                  </p>
                </div>
                <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] rounded-xl flex items-center justify-center border border-blue-100">
                  <i className="ri-dashboard-line text-white text-sm sm:text-base md:text-lg"></i>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-2.5 sm:gap-3 md:gap-4 mb-3 sm:mb-4 md:mb-6">
                <div className="bg-slate-50 p-2.5 sm:p-3 md:p-4 rounded-lg sm:rounded-xl border border-slate-200">
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 bg-gradient-to-br from-[#10B981] to-[#1E3A8A] rounded-lg flex items-center justify-center shadow-sm">
                      <i className="ri-message-3-line text-white text-xs sm:text-sm"></i>
                    </div>
                    <span className="text-lg sm:text-xl md:text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                      247
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 leading-tight" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                    {t('hero.stat1')}
                  </p>
                </div>
                <div className="bg-slate-50 p-2.5 sm:p-3 md:p-4 rounded-lg sm:rounded-xl border border-slate-200">
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 bg-gradient-to-br from-[#10B981] to-[#1E3A8A] rounded-lg flex items-center justify-center shadow-sm">
                      <i className="ri-share-line text-white text-xs sm:text-sm"></i>
                    </div>
                    <span className="text-lg sm:text-xl md:text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                      89
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 leading-tight" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                    {t('hero.stat2')}
                  </p>
                </div>
              </div>

              {/* Activity Feed */}
              <div className="space-y-2 sm:space-y-2.5 md:space-y-3">
                <div className="flex items-center gap-2 sm:gap-2.5 md:gap-3 p-2 sm:p-2.5 md:p-3 bg-white border border-slate-100 rounded-lg sm:rounded-xl shadow-sm">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 bg-gradient-to-br from-[#10B981] to-[#1E3A8A] rounded-full flex items-center justify-center flex-shrink-0 shadow-sm shadow-blue-900/10">
                    <i className="ri-check-line text-white text-xs sm:text-sm md:text-base"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-slate-700 truncate" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                      {t('hero.activity1')}
                    </p>
                    <p className="text-xs text-slate-500" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                      {t('hero.activity1Time')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-2.5 md:gap-3 p-2 sm:p-2.5 md:p-3 bg-white border border-slate-100 rounded-lg sm:rounded-xl shadow-sm">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 bg-gradient-to-br from-[#10B981] to-[#1E3A8A] rounded-full flex items-center justify-center flex-shrink-0 shadow-sm shadow-blue-900/10">
                    <i className="ri-instagram-line text-white text-xs sm:text-sm md:text-base"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-slate-700 truncate" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                      {t('hero.activity2')}
                    </p>
                    <p className="text-xs text-slate-500" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                      {t('hero.activity2Time')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Status Indicator */}
              <div className="mt-3 sm:mt-4 md:mt-6 flex items-center justify-between p-2.5 sm:p-3 md:p-4 bg-slate-100/50 rounded-lg sm:rounded-xl border border-slate-100">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full shadow-[0_0_5px_rgba(16,185,129,0.5)]"></div>
                  <span className="text-xs sm:text-sm font-medium text-slate-700" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                    {t('hero.statusActive')}
                  </span>
                </div>
                <i className="ri-shield-check-line text-emerald-500 text-base sm:text-lg md:text-xl"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-5 h-8 md:w-6 md:h-10 border border-slate-300 rounded-full flex items-start justify-center p-1.5 md:p-2">
          <div className="w-1 h-2 md:h-3 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] rounded-full shadow-sm"></div>
        </div>
      </div>
    </section>
  );
}
